import React, { useCallback } from 'react'
import { useHistory } from 'react-router'

import { useFlags } from 'launchdarkly-react-client-sdk'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import partition from 'lodash/partition'

import {
  CustomerQuery,
  DiningOptionBehavior,
  FulfillmentType,
  useAdsManagerConfigQuery
} from 'src/apollo/onlineOrdering'
import { reportErrorMessageWithData } from 'src/lib/js/clientError'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import useTracker from 'src/lib/js/hooks/useTracker'
import { t } from 'src/translations'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import {
  getReadyTimeChangeBucket,
  getScheduledFulfillmentTimeDiff,
  saveFulfillmentTime
} from 'src/public/components/default_template/online_ordering/checkout/fulfillmentTimeUtils'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { PasswordlessLoginResult } from 'src/public/components/online_ordering/CustomerContextCommon'
import {
  OrderError,
  useCheckout
} from 'src/public/components/online_ordering/CheckoutContext'
import { ORDER_KEY } from 'src/public/components/online_ordering/OrderContext'
import { useTimeBasedRules } from 'src/public/components/online_ordering/TimeBasedRuleContext'
import { CompletedOrder } from 'src/public/components/online_ordering/types'
import { Channel } from 'src/public/js/siteUtilities'
import { useExperimentUserId } from 'src/shared/components/common/ab_testing/ABTestContext'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { useRestaurantRoutes } from 'src/shared/components/common/restaurant_routes/RestaurantRoutesContext'

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'

import { getToastFundedOfferTypes } from 'public/components/default_template/offers/appliedFundedOffers'
import { getEcommShippingFee } from 'public/components/default_template/online_ordering/cart/cartUtils'
import { getTargetSegmentName } from 'public/components/default_template/online_ordering/cart/offers/OfferUtils'
import { useSpiEventTracker } from 'public/components/default_template/online_ordering/checkout/payment/useSpi'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import {
  MarketingSubscriptionChannel,
  SubscriptionSource,
  useMarketing
} from 'public/components/online_ordering/MarketingContext'
import { useOffers } from 'public/components/online_ordering/OffersContext'
import {
  PaymentOption,
  usePayment
} from 'public/components/online_ordering/PaymentContext'
import { sumByField } from 'public/components/online_ordering/reducerUtils'

import { getFirstOrThirdParty } from './loyalty/LoyaltyUtils'
import { useRefreshToastCashAmount } from './payment/ToastCashPaymentToggle'

export type Customer =
  | (CustomerQuery['customer'] & { __typename: 'Customer' })
  | undefined
  | null

export const getCustomerInfo = (customer: Customer) => {
  const phoneNum = isValidPhoneNumber(`+${customer?.phone}`)
    ? `+${customer?.phone}`
    : ''

  return {
    yourInfoPhone: phoneNum,
    yourInfoEmail: customer?.email || '',
    yourInfoFirstName: customer?.firstName || '',
    yourInfoLastName: customer?.lastName || ''
  }
}

export const submitPhoneNumberImpl = async (
  phoneNumber: string,
  passwordlessLogin: (phoneNumber: string) => Promise<PasswordlessLoginResult>,
  setSentCode: React.Dispatch<React.SetStateAction<boolean>>,
  setPasswordlessError: React.Dispatch<React.SetStateAction<string>>
) => {
  if (phoneNumber && isValidPhoneNumber(phoneNumber, 'US')) {
    const result = await passwordlessLogin(phoneNumber)
    if (!result.success) {
      setPasswordlessError(
        result.errorMessage ?? t('confirmation-code.error-sending-code')
      )
      setSentCode(false)
    } else {
      setPasswordlessError('')
      setSentCode(true)
    }
  }
}

/**
 * Extract national phone number if phone number is in E.164 format
 * @param phoneNum
 * @returns phone number
 * */
export const getNationalPhoneNumberForOrder = (phoneNum: string) => {
  return isValidPhoneNumber(phoneNum)
    ? parsePhoneNumber(phoneNum)?.nationalNumber
    : phoneNum.replace(/\D/g, '')
}

/**
 * Extract country calling code for order
 * @param phoneNum
 * @returns country calling code for phone number or undefined
 * */
export const getCallingCodeForOrder = (phoneNum: string) => {
  return isValidPhoneNumber(phoneNum)
    ? parsePhoneNumber(phoneNum).countryCallingCode
    : undefined
}

// Enums to standardize data values sent to the tracker

export enum AuthenticationStatus {
  Authenticated = 'Authenticated',
  Guest = 'Guest'
}

export enum AuthenticationSource {
  AccountCreation = 'Create a Toast account',
  AccountPage = 'Account page',
  UserNav = 'User nav',
  ExpressCheckoutToastPay = 'Express checkout with Toast Pay',
  LoyaltyCard = 'Loyalty card',
  LoyaltyCardOnConfirmationPage = 'Loyalty card on order confirmation page',
  SegmentedOfferCard = 'Segmented offer card',
  ReorderCard = 'Reorder card',
  DeliveryModal = 'Delivery modal',
  GuestCheckoutAutomatic = 'Guest checkout page (automatic)',
  ChangeAccountButton = 'Change account button',
  FreeDeliveryOfferCard = 'Free delivery offer card',
  FreeDeliveryCheckoutAlert = 'Free delivery checkout alert'
}

export function getAuthenticationStatus(customer: Customer | undefined) {
  return customer
    ? AuthenticationStatus.Authenticated
    : AuthenticationStatus.Guest
}

// Returns a string indicating how the order was paid for
export const getPaymentOption = (
  paymentType: string | null,
  paymentOption: PaymentOption | null,
  giftCardAppliedAmount: number,
  orderTotal: number,
  isInternational: boolean
) => {
  if (giftCardAppliedAmount === orderTotal) return 'GC'
  if (paymentOption === PaymentOption.UponReceipt) return 'CASH'
  if (isInternational) return 'ADYEN'
  if (paymentOption === PaymentOption.PayNow) return paymentType
  return null
}

export const useHandleCompletedOrderCallback = () => {
  const tracker = useTracker()
  const history = useHistory()
  const { cartGuid, cart, clearCart, channel } = useCart()
  const { paymentOption, tipAmount, paymentType } = usePayment()
  const {
    giftCardAppliedAmount,
    orderTotal,
    setShowAdyenOverlay,
    saveNewAddress
  } = useCheckout()
  const { confirmationPath, ecommConfirmationPath } = useRestaurantRoutes()
  const {
    ooRestaurant,
    restaurant: { pixelsV2 }
  } = useRestaurant()
  const ooClient = useOOClient()
  const { data: adsManagerData } = useAdsManagerConfigQuery({
    skip: !ooRestaurant?.guid,
    variables: { restaurantGuid: ooRestaurant?.guid ?? '' },
    client: ooClient
  })
  const adsManagerRestaurantConversionsData =
    adsManagerData?.adsManager?.adsManagerRestaurantConversionsData
  const { rankedPromoOfferDiscounts } = useOffers()
  const { selectionsInCartWithTBRs, getItemLeadTime } = useTimeBasedRules()
  const {
    subscribeToMarketing,
    subscribeToSmsMarketing,
    smsAccountEnabled,
    subscribeToEmailMarketing
  } = useMarketing()
  const userId = useExperimentUserId()
  const { ooToastCashSpend: ooToastCashSpendEnabled } = useFlags()
  const refreshToastCashBalance = useRefreshToastCashAmount()
  const isIntlRestaurant = useIsIntlRestaurant()
  const { savedAddressUsed } = useDelivery()

  return useCallback(
    (completedOrder: CompletedOrder) => {
      const { customerV3 } = completedOrder

      if (completedOrder?.guid) {
        try {
          if (cart?.order) {
            // It's safe to assume at this point that if the item has a lead time, its TBR is a lead time rule, and if it doesn't, its TBR is a preorder rule
            const [selectionsWithLeadTimeRules, selectionsWithPreorderRules] =
              partition(
                selectionsInCartWithTBRs,
                (s) => getItemLeadTime(s.itemGuid) !== undefined
              )
            const appliedPromoCode =
              cart.order.discounts.restaurantDiscount?.promoCode ?? null
            const targetSegment = getTargetSegmentName(
              appliedPromoCode,
              rankedPromoOfferDiscounts
            )
            tracker.trackConversion(
              cart,
              cartGuid!,
              pixelsV2,
              adsManagerRestaurantConversionsData
            )
            tracker.track(TrackingEvents.ORDER_PLACED, {
              cartSource: cart.cartSource,
              cartGuid: cartGuid,
              restaurantGuid: cart!.restaurant!.guid,
              diningOption: cart.diningOptionBehavior,
              fulfillmentTime: cart.fulfillmentType,
              scheduledFulfillmentTimeDiffInMinutes:
                cart.fulfillmentType === FulfillmentType.Future &&
                cart.fulfillmentDateTime
                  ? getScheduledFulfillmentTimeDiff(cart.fulfillmentDateTime)
                  : undefined,
              numItems: cart.order.numberOfSelections,
              subtotal: cart.order.preDiscountItemsSubtotal,
              tax: cart.order.taxV2,
              deliveryChargeTotal: cart.order.deliveryServiceCharges,
              gratuityServiceCharges: cart.order.gratuityServiceCharges,
              processingServiceCharges: cart.order.processingServiceCharges,
              nonDeliveryNonGratuityNonUbpServiceCharges:
                cart.order.nonDeliveryNonGratuityNonUbpServiceCharges,
              discounts: cart.order.discountsTotal,
              total: cart.order?.totalV2,
              paymentType: getPaymentOption(
                paymentType,
                paymentOption,
                giftCardAppliedAmount,
                orderTotal,
                isIntlRestaurant
              ),
              giftCardApplied: giftCardAppliedAmount > 0,
              tipAmount,
              redemptionsAppliedToCheck:
                completedOrder.discounts.loyaltyDiscounts,
              firstOrThirdParty: getFirstOrThirdParty(
                ooRestaurant?.loyaltyConfig?.programName
              ),
              leadTimeRuleItemCount: sumByField('quantity')(
                selectionsWithLeadTimeRules
              ),
              leadTimeRuleTotalPrice: sumByField('price')(
                selectionsWithLeadTimeRules
              ),
              preorderRuleItemCount: sumByField('quantity')(
                selectionsWithPreorderRules
              ),
              preorderRuleTotalPrice: sumByField('price')(
                selectionsWithPreorderRules
              ),
              useExperimentUserId: userId,
              smsAccountEnabled: smsAccountEnabled,
              smsConsentProvided: subscribeToSmsMarketing,
              smsSubscriptionIncompleteData:
                smsAccountEnabled &&
                subscribeToSmsMarketing &&
                (!customerV3 || !customerV3?.phone),
              emailConsentProvided: subscribeToEmailMarketing,
              numOffersPresent: rankedPromoOfferDiscounts?.length ?? 0,
              numPromoCodesUsed: cart.order.discounts.restaurantDiscount
                ?.promoCode
                ? 1
                : 0,
              deliveryProvider: cart.deliveryProviderInfo?.provider,
              saveNewAddress,
              savedAddressUsed:
                cart?.diningOptionBehavior === DiningOptionBehavior.Delivery
                  ? savedAddressUsed
                  : undefined,
              isTds: !!completedOrder.deliveryServiceInfo,
              toastFundedOfferTypes: getToastFundedOfferTypes(
                completedOrder.appliedToastFundedOffers
              ),
              orderGuid: completedOrder.guid,
              readyTimeChangeBucket: getReadyTimeChangeBucket(
                completedOrder,
                cartGuid,
                true
              ),
              ...(channel === Channel.ECOMM && {
                ecommFulfillmentType: !getEcommShippingFee(cart)
                  ? 'PICKUP'
                  : 'SHIPPING'
              }),
              validDeliveryAddressPresent: !!cart.order.deliveryInfo,
              amount: cart.order.preDiscountItemsSubtotal, // Subtotal excluding tax, tip, service charges, and discounts
              currency_code: ooRestaurant?.i18n?.currency ?? 'USD',
              conversion_id: completedOrder.guid ?? null,
              restaurant_guid: cart!.restaurant!.guid ?? null,
              targetSegment
            })

            if (cart.order.discounts.restaurantDiscount?.promoCode) {
              tracker.track(TrackingEvents.OFFER_APPLIED, {
                conversion_id: appliedPromoCode,
                restaurant_guid: cart!.restaurant!.guid,
                targetSegment
              })
            }
          }

          // IF SMS CHECKBOX CHECKED, SUBSCRIBE TO SMS
          if (subscribeToSmsMarketing && customerV3 && customerV3?.phone) {
            subscribeToMarketing({
              customerInput: customerV3.phone,
              firstName: customerV3.firstName || '',
              lastName: customerV3.lastName || '',
              channel: MarketingSubscriptionChannel.SMS,
              source: SubscriptionSource.CHECKOUT_PAGE
            })
          }

          // IF EMAIL CHECKBOX CHECKED, SUBSCRIBE TO EMAIL
          if (subscribeToEmailMarketing && customerV3 && customerV3?.email) {
            subscribeToMarketing({
              customerInput: customerV3.email,
              firstName: customerV3.firstName || '',
              lastName: customerV3.lastName || '',
              channel: MarketingSubscriptionChannel.EMAIL,
              source: SubscriptionSource.CHECKOUT_PAGE
            })
          }

          // We save the fulfillment time to local storage here so that it is available on order confirmation based on the order guid because the cart is cleared
          saveFulfillmentTime(cart, completedOrder.guid)
        } catch (e) {
          reportErrorMessageWithData('Error completing order', {
            name: e.name,
            message: e.message,
            cause: e.cause,
            stack: e.stack
          })
          // Log this error and ignore it. Any errors raised here will try to be handled by the SPI SDK.
          // This can cause the SPI SDK to throw an error because it doesn't expect this error. That SPI
          // SDK error will then make it back to us and we'll display an error to the user even though the
          // order was successfully placed.
        }
        clearCart()
        setShowAdyenOverlay(false)
        localStorage.setItem(ORDER_KEY, completedOrder.guid)
        if (ooToastCashSpendEnabled) {
          refreshToastCashBalance()
        }
        const basePath =
          channel === Channel.ECOMM && ecommConfirmationPath
            ? ecommConfirmationPath
            : confirmationPath
        const locationGuid =
          completedOrder.restaurant?.guid || cart?.restaurant?.guid || ''
        const pathWithOrder = `${basePath}/${completedOrder.guid}`
        const separator = pathWithOrder.includes('?') ? '&' : '?'
        const confirmUrl = locationGuid
          ? `${pathWithOrder}${separator}locationGuid=${locationGuid}`
          : pathWithOrder
        history.push(confirmUrl, { completedOrder })
      }
    },
    [
      cart,
      subscribeToSmsMarketing,
      subscribeToEmailMarketing,
      clearCart,
      setShowAdyenOverlay,
      history,
      confirmationPath,
      ecommConfirmationPath,
      channel,
      selectionsInCartWithTBRs,
      tracker,
      cartGuid,
      pixelsV2,
      paymentType,
      paymentOption,
      giftCardAppliedAmount,
      orderTotal,
      tipAmount,
      ooRestaurant?.loyaltyConfig?.programName,
      adsManagerRestaurantConversionsData,
      userId,
      smsAccountEnabled,
      getItemLeadTime,
      subscribeToMarketing,
      rankedPromoOfferDiscounts,
      ooToastCashSpendEnabled,
      refreshToastCashBalance,
      isIntlRestaurant,
      saveNewAddress,
      savedAddressUsed
    ]
  )
}

export const useHandleValidationError = () => {
  const { trackSpi } = useSpiEventTracker()
  const { setOrderError } = useCheckout()

  const onError = useCallback(
    (orderError: OrderError) => {
      setOrderError(orderError)
      trackSpi('temp_SPI_precheckout_cart_validation_failed', orderError)
      reportErrorMessageWithData('Invalid cart', orderError)
    },
    [setOrderError, trackSpi]
  )

  return onError
}

/**
 * Mutate a DeliveryInfoInput's `notes` field to account for the contactless delivery short-cut.
 * Until contactless delivery is first-class in the DeliveryInfo domain, we are passing through
 * a (customer-hidden) string marker in the delivery notes field for TDS to interpret. We remove
 * this marker for save address operations(consumer-app-bff) such that it is not persisted
 * and surfaced to the guest.
 *
 * Reference: https://toasttab.atlassian.net/browse/WOO-3184
 */
const CONTACTLESS_MARKER = 'PLEASE MAKE THIS CONTACTLESS DELIVERY '
export const applyContactlessIndicator = (deliveryInstructions: string) =>
  CONTACTLESS_MARKER.concat(deliveryInstructions)
export const containsContactlessIndicator = (deliveryInstructions: string) =>
  deliveryInstructions.startsWith(CONTACTLESS_MARKER)
export const removeContactlessIndicator = (deliveryInstructions: string) =>
  containsContactlessIndicator(deliveryInstructions)
    ? deliveryInstructions.slice(CONTACTLESS_MARKER.length)
    : deliveryInstructions
